import { fmtInteiro } from './moeda';

/* ============================================================
   ALERTAS DAS VIATURAS — regras num sítio só

   As mesmas três regras decidem duas coisas: que campos ficam a vermelho no
   cartão da viatura e que número aparece na barra lateral. Estavam a nascer
   duplicadas — uma cópia no ecrã e outra na contagem — e duas cópias da mesma
   regra acabam sempre por divergir: o menu anunciava um número e o ecrã
   mostrava outro. Vivem aqui, e ambos as importam.
   ============================================================ */

export const DIAS_ALERTA_PRAZO = 30;      // seguro e inspeção
export const KMS_ALERTA_REVISAO = 2500;   // revisão

/* Verdadeiro quando a data está a um mês ou menos — incluindo já passada, que é
   o caso mais urgente de todos. Comparação a zero horas dos dois lados, senão a
   hora a que se abre o ecrã mudava a contagem de dias. */
export const prazoEmAlerta = (valor) => {
    if (!valor) return false;
    const d = new Date(String(valor).slice(0, 10) + 'T00:00:00');
    if (isNaN(d.getTime())) return false;
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    return Math.round((d - hoje) / 86400000) <= DIAS_ALERTA_PRAZO;
};

/* Quilómetros que faltam até à revisão: o que está escrito na ficha menos a
   última leitura do conta-quilómetros. A ficha é texto livre — tanto pode ter
   "150.000 km" como "após o inverno" — por isso a conta só se faz quando de lá
   se consegue tirar um número e existe uma leitura com que o comparar. Nos
   outros casos mostra-se o que lá está escrito, sem inventar. */
export const kmsParaRevisao = (veiculo) => {
    const escrito = String(veiculo?.proxima_revisao || '').trim();
    if (!escrito) return { texto: 'N/D' };
    // Aceita "150.000", "150 000" e "150000"; o ponto e o espaço são
    // separadores de milhares, não decimais.
    const encontrado = escrito.match(/\d[\d.\s]*/);
    const alvo = encontrado ? parseInt(encontrado[0].replace(/[.\s]/g, ''), 10) : NaN;
    if (!Number.isFinite(alvo) || veiculo?.ultimo_km == null) return { texto: escrito };
    const faltam = alvo - Number(veiculo.ultimo_km);
    return {
        texto: `${faltam < 0 ? '−' : ''}${fmtInteiro(Math.abs(faltam))} km`,
        ultrapassado: faltam < 0,
        alerta: faltam <= KMS_ALERTA_REVISAO,
    };
};

/* A revisão tem dois gatilhos e vale o que vier primeiro: a quilometragem
   escrita na ficha, ou uma data limite. Um carro pouco usado chega à data sem
   chegar aos quilómetros; um muito usado chega aos quilómetros muito antes da
   data. Basta um dos dois disparar. */
export const revisaoEmAlerta = (veiculo) =>
    !!kmsParaRevisao(veiculo).alerta || prazoEmAlerta(veiculo?.proxima_revisao_data);

/* O valor por que cada alerta se identifica. É o que fica guardado quando se
   dá o alerta por tratado: se a data ou a quilometragem mudarem depois, o
   guardado deixa de bater certo e o alerta volta a aparecer. */
export const referenciaAlerta = (veiculo, campo) => {
    if (campo === 'seguro') return String(veiculo?.validade_seguro || '').slice(0, 10);
    if (campo === 'inspecao') return String(veiculo?.validade_inspecao || '').slice(0, 10);
    // O consumo identifica-se pelo abastecimento que o produziu: dar por visto
    // silencia ESTE desvio, não os que vierem a seguir.
    if (campo === 'consumo') return String(veiculo?.consumo?.ultimo_abastecimento || '');
    // Os dois gatilhos entram na referência: mudar só a data também tem de
    // reabrir um alerta que já tinha sido dado por tratado.
    return String(veiculo?.proxima_revisao || '').trim()
        + '|' + String(veiculo?.proxima_revisao_data || '').slice(0, 10);
};

/* Verdadeiro quando o alerta deste campo já foi dado por tratado E o valor
   continua a ser o mesmo de então. */
export const alertaTratado = (veiculo, campo) => {
    const tratados = veiculo?.alertas_tratados || {};
    if (!(campo in tratados)) return false;
    return String(tratados[campo] || '') === referenciaAlerta(veiculo, campo);
};

/* Se o campo está em alerta E esse alerta ainda não foi tratado — que é o que
   decide o vermelho no ecrã e o número na barra lateral. */
export const alertaPorTratar = (veiculo, campo) => {
    if (veiculo?.alugada) return false;
    if (alertaTratado(veiculo, campo)) return false;
    if (campo === 'revisao') return revisaoEmAlerta(veiculo);
    if (campo === 'consumo') return consumoEmAlerta(veiculo);
    return prazoEmAlerta(campo === 'seguro' ? veiculo?.validade_seguro : veiculo?.validade_inspecao);
};

/* O último abastecimento afastou-se do que era normal nesta viatura? O cálculo
   e os limites vivem no servidor (backend/utils/consumoVeiculos.js), que é
   quem tem os abastecimentos todos; aqui só se lê o veredicto. */
export const consumoEmAlerta = (veiculo) => {
    if (veiculo?.alugada) return false;
    const a = veiculo?.consumo?.alerta;
    return a === 'atencao' || a === 'grave';
};

/* Quantos dos quatro dados do resumo estão em alerta, numa viatura. */
export const contarAlertas = (veiculo) =>
    ['seguro', 'inspecao', 'revisao', 'consumo'].filter(c => alertaPorTratar(veiculo, c)).length;

/* Total de toda a frota — o número que vai para a barra lateral. */
export const totalAlertas = (veiculos) =>
    (Array.isArray(veiculos) ? veiculos : []).reduce((s, v) => s + contarAlertas(v), 0);
